import React, { ReactNode } from "react";
import { StyleSheet, Text, View } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { colors, radii, spacing, typographyScale } from "../../../../styles/design-system";
import type { CalloutVariant } from "../../../common/general/CustomMarkdown/calloutPlugin";

export interface CalloutCardProps {
  variant: CalloutVariant;
  children: ReactNode;
}

const TASK_GRADIENT: [string, string] = [colors.brand[50], colors.white];

const VARIANT_STYLES: Record<
  CalloutVariant,
  { label: string; border: string; labelColor: string; background?: string }
> = {
  task: {
    label: "TASK",
    border: colors.brand[100],
    labelColor: colors.brand[700],
  },
  scenario: {
    label: "SCENARIO",
    border: colors.accent.teal200,
    labelColor: colors.accent.teal700,
    background: colors.accent.teal50,
  },
};

export const CalloutCard = ({ variant, children }: CalloutCardProps) => {
  const v = VARIANT_STYLES[variant];
  const containerStyle = [
    styles.container,
    { borderColor: v.border },
    v.background ? { backgroundColor: v.background } : null,
  ];

  const inner = (
    <>
      <Text style={[styles.label, { color: v.labelColor }]}>{v.label}</Text>
      {children}
    </>
  );

  return (
    <View accessibilityRole="group" accessibilityLabel={`${v.label} callout`} style={containerStyle}>
      {variant === "task" ? (
        <LinearGradient colors={TASK_GRADIENT} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={styles.padded}>
          {inner}
        </LinearGradient>
      ) : (
        <View style={styles.padded}>{inner}</View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: radii.lg,
    marginVertical: spacing.xs,
    overflow: "hidden",
  },
  padded: {
    padding: spacing.md,
  },
  label: {
    fontSize: typographyScale.textXs.fontSize,
    fontWeight: "700",
    letterSpacing: 0.5,
    marginBottom: spacing.xs,
  },
});

CalloutCard.displayName = "CalloutCard";

export default CalloutCard;
